import { contaSessionStorage } from 'dashboard/helper/accountScopedStorage';
// SiteUP CRM: caminho unico de criacao de tarefa.
//
// Antes desta fatia existiam tres formularios de nova tarefa com regras
// diferentes (editor grande, dialogo do painel do contato e a linha do chip da
// conversa). O chip gravava o literal 'pending', o dialogo criava subtarefas e
// o editor nao criava nenhuma delas antes de salvar. Aqui mora a regra unica:
// status inicial vindo do catalogo, vinculo pela origem, subtarefas e anexo
// pendente criados logo depois da tarefa.
//
// Logica pura mais orquestracao com dependencias injetaveis, para o vitest
// cobrir sem montar tela.

import TasksAPI from 'dashboard/api/tasks';
import TaskSubtasksAPI from 'dashboard/api/taskSubtasks';
import { withDefaultStatuses } from 'dashboard/store/modules/tasks/defaultStatuses';

// Hora padrao de vencimento quando o usuario escolhe so o dia.
export const DEFAULT_DUE_HOUR = 9;

/**
 * Status com que toda tarefa nova nasce: o primeiro nao terminal do catalogo
 * da conta. Nunca o literal 'pending', que pode nem existir na conta.
 *
 * @param {Array} statuses catalogo vindo de tasks/getStatuses
 * @returns {string} chave do status
 */
export function initialStatusKey(statuses) {
  const catalog = withDefaultStatuses(statuses);
  return (catalog.find(status => !status.is_terminal) || catalog[0])?.key || '';
}

const startOfDay = date => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
};

const addDays = (date, days) => {
  const value = startOfDay(date);
  value.setDate(value.getDate() + days);
  return value;
};

/**
 * Primeiro dia util estritamente depois de amanha. Em semana normal isso cai
 * na segunda; perto do fim de semana cai no proximo dia util, que e o que a
 * decisao 20 pede.
 *
 * @param {Date} now instante de referencia
 * @returns {Date} data no inicio do dia
 */
export function nextWorkday(now = new Date()) {
  const value = addDays(now, 2);
  while (value.getDay() === 0 || value.getDay() === 6) {
    value.setDate(value.getDate() + 1);
  }
  return value;
}

/**
 * Data (inicio do dia) de cada atalho de prazo.
 *
 * @param {string} key today, tomorrow ou workday
 * @param {Date} now instante de referencia
 * @returns {Date|null}
 */
export function dueChipDate(key, now = new Date()) {
  if (key === 'today') return startOfDay(now);
  if (key === 'tomorrow') return addDays(now, 1);
  if (key === 'workday') return nextWorkday(now);
  return null;
}

/**
 * Data local no formato aceito pelo seletor de data do produto (AAAA-MM-DD).
 *
 * @param {Date} date
 * @returns {string|null}
 */
export function toIsoDate(date) {
  if (!date || Number.isNaN(new Date(date).getTime())) return null;
  const value = new Date(date);
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}

/**
 * Junta dia e hora opcional num instante ISO.
 *
 * @param {string} isoDate AAAA-MM-DD
 * @param {string} time HH:MM (opcional)
 * @returns {{value: (string|null), invalid: boolean}}
 */
export function buildDueAt(isoDate, time = '') {
  if (!isoDate) return { value: null, invalid: false };
  const date = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!date) return { value: null, invalid: true };
  const clock = String(time || '').match(/^(\d{2}):(\d{2})$/);
  if (time && !clock) return { value: null, invalid: true };
  const hour = clock ? Number(clock[1]) : DEFAULT_DUE_HOUR;
  const minute = clock ? Number(clock[2]) : 0;
  if (hour > 23 || minute > 59) return { value: null, invalid: true };
  const local = new Date(
    Number(date[1]),
    Number(date[2]) - 1,
    Number(date[3]),
    hour,
    minute
  );
  const consistent =
    local.getFullYear() === Number(date[1]) &&
    local.getMonth() === Number(date[2]) - 1 &&
    local.getDate() === Number(date[3]);
  return consistent
    ? { value: local.toISOString(), invalid: false }
    : { value: null, invalid: true };
}

/**
 * Vinculo da tarefa traduzido para os campos que a API espera. Cartao, conversa
 * e contato sao exclusivos entre si; a conversa carrega junto o contato dela
 * quando conhecido, igual ao dialogo antigo do painel do contato.
 *
 * @param {object} link { type, id, contactId }
 * @returns {object}
 */
export function linkFields(link) {
  const fields = {
    kanban_item_id: null,
    conversation_display_id: null,
    contact_id: null,
  };
  if (!link || !link.type || !link.id) return fields;
  const id = Number(link.id);
  if (!Number.isSafeInteger(id) || id <= 0) return fields;
  if (link.type === 'kanban_item') fields.kanban_item_id = id;
  if (link.type === 'conversation') {
    fields.conversation_display_id = id;
    const contactId = Number(link.contactId);
    if (Number.isSafeInteger(contactId) && contactId > 0)
      fields.contact_id = contactId;
  }
  if (link.type === 'contact') fields.contact_id = id;
  return fields;
}

/**
 * Corpo do POST /tasks. Mesmo payload para linha rapida, formulario unico e
 * chip da conversa.
 *
 * @param {object} draft rascunho do formulario
 * @param {Array} statuses catalogo de status da conta
 * @returns {object}
 */
export function buildTaskPayload(draft = {}, statuses = []) {
  const description = (draft.description || '').trim();
  return {
    title: (draft.title || '').trim(),
    description: description || null,
    priority: draft.priority || 'medium',
    status_key: draft.statusKey || initialStatusKey(statuses),
    assigned_to_user_id: draft.assignedToUserId || null,
    due_at: draft.dueAt || null,
    reminder_offsets: Array.isArray(draft.reminderOffsets)
      ? draft.reminderOffsets
      : [],
    recurrence_rule: draft.recurrenceRule || null,
    label_ids: Array.isArray(draft.labelIds) ? draft.labelIds : [],
    ...linkFields(draft.link),
  };
}

/**
 * Subtarefas que ainda precisam ser criadas, com o indice de origem para o
 * formulario marcar quais entraram e repetir so as que faltaram.
 *
 * @param {Array} subtasks
 * @returns {Array<{index: number, title: string, position: number}>}
 */
export function pendingSubtasks(subtasks = []) {
  return (subtasks || [])
    .map((subtask, index) => ({
      index,
      title: (subtask?.title || '').trim(),
      position:
        typeof subtask?.position === 'number' ? subtask.position : index,
      created: subtask?.created === true,
    }))
    .filter(subtask => subtask.title && !subtask.created);
}

const defaultDeps = {
  createTask: payload => TasksAPI.create(payload).then(({ data }) => data),
  createSubtask: (taskId, body) =>
    TaskSubtasksAPI.create(taskId, body).then(({ data }) => data),
  uploadAttachment: async (taskId, file) => {
    const AttachmentsAPI = (await import('dashboard/api/taskAttachments'))
      .default;
    const { data } = await AttachmentsAPI.upload(
      taskId,
      file,
      crypto.randomUUID()
    );
    return data;
  },
};

/**
 * Cria a tarefa e, em seguida, as subtarefas e o anexo pendente.
 *
 * A tarefa criada NAO e desfeita quando uma subtarefa falha: o retorno diz
 * quais faltaram para o formulario oferecer nova tentativa so delas. Passar
 * `draft.task` (tarefa ja criada numa tentativa anterior) pula o POST da
 * tarefa e repete apenas o que faltou.
 *
 * @param {object} draft rascunho, com subtasks e attachmentFile opcionais
 * @param {object} options { statuses, deps }
 * @returns {Promise<{task: object, failedSubtasks: Array, createdSubtaskIndexes: Array<number>, attachment: object|null, attachmentError: string}>}
 */
export async function createTaskWithExtras(draft = {}, options = {}) {
  const { statuses = [], deps = {} } = options;
  const api = { ...defaultDeps, ...deps };

  const task =
    draft.task && draft.task.id
      ? draft.task
      : await api.createTask(buildTaskPayload(draft, statuses));
  if (!task?.id) throw new Error('Missing created task ID');

  const failedSubtasks = [];
  const createdSubtaskIndexes = [];
  // Sequencial de proposito: a posicao de cada subtarefa e a ordem da lista.
  await pendingSubtasks(draft.subtasks).reduce(async (previous, subtask) => {
    await previous;
    try {
      await api.createSubtask(task.id, {
        title: subtask.title,
        position: subtask.position,
      });
      createdSubtaskIndexes.push(subtask.index);
    } catch {
      failedSubtasks.push(subtask);
    }
  }, Promise.resolve());

  let attachment = null;
  let attachmentError = '';
  if (draft.attachmentFile) {
    try {
      attachment = await api.uploadAttachment(task.id, draft.attachmentFile);
    } catch {
      attachmentError = 'UPLOAD_ERROR';
    }
  }

  return {
    task,
    failedSubtasks,
    createdSubtaskIndexes,
    attachment,
    attachmentError,
  };
}

// Titulo digitado fora da tela de Tarefas (chip da conversa) e levado para o
// formulario unico. Vai por sessionStorage e nao pela URL, porque titulo de
// tarefa pode ter dado pessoal. Vence sozinho para nao vazar para outra criacao.
const TASK_DRAFT_TITLE_KEY = 'siteup_task_draft_title';
const TASK_DRAFT_TITLE_TTL_MS = 60000;

export const writeTaskDraftTitle = (title, now = Date.now()) => {
  const typed = String(title || '').trim();
  if (!typed) return;
  // Sem conta no contexto nao grava: titulo de tarefa pode ter dado pessoal e
  // uma chave global apareceria no formulario da outra conta.
  contaSessionStorage.setJSON(TASK_DRAFT_TITLE_KEY, { title: typed, at: now });
};

export const readTaskDraftTitle = (now = Date.now()) => {
  const parsed = contaSessionStorage.getJSON(TASK_DRAFT_TITLE_KEY, null);
  if (!parsed?.title || now - Number(parsed.at) > TASK_DRAFT_TITLE_TTL_MS) {
    contaSessionStorage.removeItem(TASK_DRAFT_TITLE_KEY);
    return '';
  }
  return String(parsed.title);
};

export const clearTaskDraftTitle = () => {
  contaSessionStorage.removeItem(TASK_DRAFT_TITLE_KEY);
};
